<template>
  <Form
    :form-id="formId"
    :initial-values="initialValues"
    action="#"
    class="section section-row div-5 expense-row vp-row-form"
    :class="{ dirty }"
  >
    <HiddenInput input-id="id" />

    <div class="double-f">
      <VPInput
        ref="nameRef"
        :disabled="disabled"
        input-id="name"
        placeholder="Add expense"
      />
    </div>
    <div
      class="flex relative"
      :class="{ 'has-value': hasType }"
    >
      <VPDropdown
        input-id="calculationType"
        placeholder="Choose condition"
        :options="calculationTypeOptions"
        @change="onTypeChange"
      />
    </div>
    <div>
      <VPPercentageInput
        v-if="calculationType === 'gbor_percentage'"
        ref="calculationValueRef"
        :disabled="disabled"
        input-id="calculationValue"
        placeholder="Add data"
        hide-empty-input
      />
      <VPPriceInput
        v-else
        ref="calculationValueRef"
        :disabled="disabled"
        input-id="calculationValue"
        placeholder="Add data"
        hide-empty-input
      />
    </div>
    <div class="right-field overflow-x-auto">
      {{ expenseAmountDisplay }}
    </div>
  </Form>
</template>

<script setup lang="ts">
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import VPInput from '@/components/form/VPInput.vue';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import VPPercentageInput from '@/components/form/VPPercentageInput.vue';
import VPDropdown from '@/components/form/VPDropdown.vue';
import { VariableExpenseEnum } from '@/generated/graphql';
import { useOfferSheetFormStore } from '@/stores/offer-sheet-form';
import { formatCurrency } from '@/helpers/currency';

const formsStore = useFormsStore();
const offerSheetFormStore = useOfferSheetFormStore();

const dirty = computed(() => formsStore.isFormDirty(props.formId));

const expenseAmount = computed(() => offerSheetFormStore.expenseAmount(props.formId));

const expenseAmountDisplay = computed(() => {
  if (!formsStore.fieldGetValue(props.formId, 'id')) {
    return '';
  }

  const display = isNaN(expenseAmount.value) ? 0 : expenseAmount.value;
  return formatCurrency(display, { onZero: '-' });
});

const hasType = computed(() => !!formsStore.fieldGetValue(props.formId, 'calculationType'));

const calculationType = computed(() => formsStore.fieldGetValue(props.formId, 'calculationType'));

const calculationTypeOptions = [
  {
    name: '% of GBOR',
    value: VariableExpenseEnum.GborPercentage,
  },
  {
    name: 'Per Attendee',
    value: VariableExpenseEnum.PerAttendee,
  },
  {
    name: 'Per ticket',
    value: VariableExpenseEnum.PerTicket,
  },
];

interface Props {
  formId: string;
  initialValues: unknown;
  disabled?: boolean;
}

const props = defineProps<Props>();

const nameRef = ref<VPInput>(null);
const amountRef = ref<VPPriceInput>(null);
const calculationValueRef = ref<VPInput>(null);

const emit = defineEmits<{
  (e: 'blur'): void;
  (e: 'save'): void;
  (e: 'clear'): void;
}>();

const refs = [nameRef, amountRef, calculationValueRef];

const activeElement = ref<EventTarget | null>(null);

const focusChanged = (e: Event) => {
  activeElement.value = e.target;
};

onMounted(() => {
  document.addEventListener('focusin', focusChanged);
});
onBeforeUnmount(() => {
  document.removeEventListener('focusin', focusChanged);
});

const hasFocus = computed(() =>
  refs.some(r => r.value?.inputRef && activeElement.value && r.value?.inputRef === activeElement.value),
);

const hasValue = computed(() => {
  return ['name', 'calculationValue'].some(f => formsStore.fieldGetValue(props.formId, f));
});

watch(hasFocus, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    if (hasValue.value) {
      emit('blur');
    } else {
      emit('clear');
    }
  }
});

const onTypeChange = () => {
  emit('save');
};
</script>
